import type {
  AutomationExecution,
  AutomationRule,
  FlagForReviewConfig,
} from '../api/automation';

export type AutomationOutcomeDestination =
  | 'dashboard'
  | 'rebalancing'
  | 'portfolio-analytics'
  | 'risk-intelligence';

export type AutomationOutcomeState = 'matched' | 'unmatched' | 'no-execution-yet';

export interface AutomationOutcomeHandoffContext {
  handoff: 'automation-outcome';
  source: 'automation';
  destination: AutomationOutcomeDestination;
  outcomeState: AutomationOutcomeState;
  ruleLabel: string;
  issuedAt: string;
  occurredAt?: string | null;
  preparedOutcomeText?: string;
  waitingReason?: string;
  continuationLabel?: string;
  ruleId?: string;
  executionId?: string;
}

const HANDOFF_MAX_AGE_MS = 1000 * 60 * 60 * 4;
const STORAGE_PREFIX = 'pinecurl.automation-arrival';

function isDestination(value: string | null): value is AutomationOutcomeDestination {
  return (
    value === 'dashboard' ||
    value === 'rebalancing' ||
    value === 'portfolio-analytics' ||
    value === 'risk-intelligence'
  );
}

function isValidTimestamp(value: string | null | undefined): value is string {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
}

function isOutcomeState(value: string | null | undefined): value is AutomationOutcomeState {
  return value === 'matched' || value === 'unmatched' || value === 'no-execution-yet';
}

function setIfPresent(params: URLSearchParams, key: string, value: string | null | undefined) {
  if (!value) return;
  params.set(key, value);
}

function getStorageKey(destination: AutomationOutcomeDestination, userId: string) {
  return `${STORAGE_PREFIX}.${destination}.${userId}`;
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function validateIssuedAt(issuedAt: string) {
  const issuedAtMs = new Date(issuedAt).getTime();
  const now = Date.now();

  if (issuedAtMs > now + 60_000) {
    return false;
  }

  return now - issuedAtMs <= HANDOFF_MAX_AGE_MS;
}

function validateOccurredAt(occurredAt: string | null | undefined) {
  if (!occurredAt || !isValidTimestamp(occurredAt)) {
    return false;
  }

  return new Date(occurredAt).getTime() <= Date.now() + 60_000;
}

function getPreparedOutcomeText(
  rule: AutomationRule,
  execution?: AutomationExecution | null,
): string {
  if (execution?.action_taken?.trim()) {
    return execution.action_taken.trim();
  }

  if (rule.action_type === 'create_rebalancing_plan') {
    return 'Prepared a rebalancing follow-up for manual review.';
  }

  if (rule.action_type === 'flag_for_review') {
    const reason = (rule.action_config as FlagForReviewConfig).reason?.trim();
    return reason ? `Flagged for review: ${reason}` : 'Flagged this portfolio state for manual review.';
  }

  return 'Prepared an advisory notification for this saved rule.';
}

function getWaitingReason(rule: AutomationRule, outcomeState: AutomationOutcomeState): string {
  if (outcomeState === 'unmatched') {
    return 'Pinecurl is still watching this saved rule and has not recorded a match yet.';
  }

  if (rule.is_active) {
    return 'Pinecurl is waiting for the first recorded execution on this saved rule.';
  }

  return 'This saved rule is paused, so Pinecurl is not recording a live execution yet.';
}

function sanitizeAutomationOutcomeHandoff(
  value: unknown,
  expectedDestination?: AutomationOutcomeDestination,
): AutomationOutcomeHandoffContext | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const raw = value as Partial<AutomationOutcomeHandoffContext> & {
    rule?: string | null;
    matchedRule?: string | null;
    state?: string | null;
    outcome?: string | null;
    nextStep?: string | null;
  };

  const destination = typeof raw.destination === 'string' ? raw.destination : null;
  const issuedAt = typeof raw.issuedAt === 'string' ? raw.issuedAt : null;
  const ruleLabel =
    (typeof raw.ruleLabel === 'string' && raw.ruleLabel.trim()) ||
    (typeof raw.rule === 'string' && raw.rule.trim()) ||
    (typeof raw.matchedRule === 'string' && raw.matchedRule.trim()) ||
    null;
  const outcomeState = isOutcomeState(raw.outcomeState)
    ? raw.outcomeState
    : isOutcomeState(raw.state)
      ? raw.state
      : 'matched';

  if (
    raw.handoff !== 'automation-outcome' ||
    raw.source !== 'automation' ||
    !isDestination(destination) ||
    (expectedDestination && destination !== expectedDestination) ||
    !issuedAt ||
    !validateIssuedAt(issuedAt) ||
    !ruleLabel
  ) {
    return null;
  }

  const occurredAt = typeof raw.occurredAt === 'string' ? raw.occurredAt : null;
  if (outcomeState === 'matched' && !validateOccurredAt(occurredAt)) {
    return null;
  }

  if (outcomeState !== 'matched' && occurredAt && !validateOccurredAt(occurredAt)) {
    return null;
  }

  const preparedOutcomeText =
    typeof raw.preparedOutcomeText === 'string' && raw.preparedOutcomeText.trim()
      ? raw.preparedOutcomeText.trim()
      : typeof raw.outcome === 'string' && raw.outcome.trim()
        ? raw.outcome.trim()
        : undefined;
  const waitingReason =
    typeof raw.waitingReason === 'string' && raw.waitingReason.trim()
      ? raw.waitingReason.trim()
      : undefined;
  const continuationLabel =
    typeof raw.continuationLabel === 'string' && raw.continuationLabel.trim()
      ? raw.continuationLabel.trim()
      : typeof raw.nextStep === 'string' && raw.nextStep.trim()
        ? raw.nextStep.trim()
        : undefined;

  return {
    handoff: 'automation-outcome',
    source: 'automation',
    destination,
    outcomeState,
    ruleLabel,
    issuedAt,
    occurredAt,
    preparedOutcomeText,
    waitingReason,
    continuationLabel,
    ruleId: typeof raw.ruleId === 'string' && raw.ruleId.trim() ? raw.ruleId.trim() : undefined,
    executionId:
      typeof raw.executionId === 'string' && raw.executionId.trim() ? raw.executionId.trim() : undefined,
  };
}

export function resolveAutomationOutcomeState(
  rule: AutomationRule,
  execution?: AutomationExecution | null,
): AutomationOutcomeState {
  if (execution || isValidTimestamp(rule.last_triggered_at)) {
    return 'matched';
  }

  if (rule.is_active) {
    return 'unmatched';
  }

  return 'no-execution-yet';
}

export function buildAutomationOutcomeHandoffContext(
  rule: AutomationRule,
  execution?: AutomationExecution | null,
): AutomationOutcomeHandoffContext | null {
  const destination = resolveAutomationOutcomeDestination(rule, execution);
  const ruleLabel = rule.name.trim();

  if (!destination || !ruleLabel) {
    return null;
  }

  const outcomeState = resolveAutomationOutcomeState(rule, execution);
  const occurredAt = execution?.triggered_at ?? rule.last_triggered_at ?? null;

  if (outcomeState === 'matched' && !validateOccurredAt(occurredAt)) {
    return null;
  }

  return {
    handoff: 'automation-outcome',
    source: 'automation',
    destination,
    outcomeState,
    ruleLabel,
    issuedAt: new Date().toISOString(),
    occurredAt: outcomeState === 'matched' ? occurredAt : null,
    preparedOutcomeText:
      outcomeState === 'matched' ? getPreparedOutcomeText(rule, execution) : undefined,
    waitingReason:
      outcomeState === 'matched' ? undefined : getWaitingReason(rule, outcomeState),
    continuationLabel: getAutomationOutcomeContinuationLabel(destination),
    ruleId: rule.id || undefined,
    executionId: execution?.id || undefined,
  };
}

export function resolveAutomationOutcomeDestination(
  rule: AutomationRule,
  execution?: AutomationExecution | null,
): AutomationOutcomeDestination | null {
  if (rule.action_type === 'create_rebalancing_plan') {
    return 'dashboard';
  }

  if (rule.action_type === 'send_notification' && rule.trigger_type === 'health_score_below') {
    return 'portfolio-analytics';
  }

  if (
    rule.action_type === 'flag_for_review' &&
    (rule.trigger_type === 'portfolio_drawdown_exceeds' || rule.trigger_type === 'var_exceeds')
  ) {
    return 'risk-intelligence';
  }

  const snapshot = execution?.trigger_snapshot ?? null;
  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  if (
    rule.action_type === 'flag_for_review' &&
    ('portfolio_drawdown_pct' in snapshot || 'var_value' in snapshot)
  ) {
    return 'risk-intelligence';
  }

  if (rule.action_type === 'send_notification' && 'health_score' in snapshot) {
    return 'portfolio-analytics';
  }

  return null;
}

export function getAutomationOutcomeHandoffPath(
  rule: AutomationRule,
  execution?: AutomationExecution | null,
): string | null {
  const handoff = buildAutomationOutcomeHandoffContext(rule, execution);

  if (!handoff) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('handoff', 'automation-outcome');
  params.set('source', 'automation');
  params.set('destination', handoff.destination);
  params.set('state', handoff.outcomeState);
  params.set('rule', handoff.ruleLabel);
  params.set('ruleLabel', handoff.ruleLabel);
  params.set('issuedAt', handoff.issuedAt);
  setIfPresent(params, 'occurredAt', handoff.occurredAt ?? undefined);
  setIfPresent(params, 'outcome', handoff.preparedOutcomeText);
  setIfPresent(params, 'waitingReason', handoff.waitingReason);
  setIfPresent(params, 'nextStep', handoff.continuationLabel);
  setIfPresent(params, 'ruleId', handoff.ruleId);
  setIfPresent(params, 'executionId', handoff.executionId);

  const basePath =
    handoff.destination === 'dashboard'
      ? '/dashboard'
      : handoff.destination === 'rebalancing'
      ? '/rebalancing'
      : handoff.destination === 'portfolio-analytics'
        ? '/portfolio-analytics'
        : '/risk-intelligence';

  return `${basePath}?${params.toString()}`;
}

export function readAutomationOutcomeHandoff(
  searchParams: URLSearchParams,
  expectedDestination?: AutomationOutcomeDestination,
): AutomationOutcomeHandoffContext | null {
  if (searchParams.get('handoff') !== 'automation-outcome' || searchParams.get('source') !== 'automation') {
    return null;
  }

  return sanitizeAutomationOutcomeHandoff(
    {
      handoff: searchParams.get('handoff'),
      source: searchParams.get('source'),
      destination: searchParams.get('destination'),
      outcomeState: searchParams.get('state'),
      ruleLabel: searchParams.get('ruleLabel'),
      rule: searchParams.get('rule'),
      occurredAt: searchParams.get('occurredAt'),
      issuedAt: searchParams.get('issuedAt'),
      preparedOutcomeText: searchParams.get('outcome'),
      waitingReason: searchParams.get('waitingReason'),
      continuationLabel: searchParams.get('nextStep'),
      ruleId: searchParams.get('ruleId'),
      executionId: searchParams.get('executionId'),
    },
    expectedDestination,
  );
}

export function readPersistedAutomationOutcomeHandoff(
  destination: AutomationOutcomeDestination,
  userId?: string | null,
): AutomationOutcomeHandoffContext | null {
  if (!userId || !canUseStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(destination, userId));
    if (!raw) {
      return null;
    }

    const parsed = sanitizeAutomationOutcomeHandoff(JSON.parse(raw), destination);
    if (!parsed) {
      window.localStorage.removeItem(getStorageKey(destination, userId));
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writePersistedAutomationOutcomeHandoff(
  destination: AutomationOutcomeDestination,
  userId: string | null | undefined,
  handoff: AutomationOutcomeHandoffContext | null,
) {
  if (!userId || !canUseStorage()) {
    return;
  }

  try {
    if (!handoff) {
      window.localStorage.removeItem(getStorageKey(destination, userId));
      return;
    }

    const sanitized = sanitizeAutomationOutcomeHandoff(handoff, destination);
    if (!sanitized) {
      window.localStorage.removeItem(getStorageKey(destination, userId));
      return;
    }

    const existing = readPersistedAutomationOutcomeHandoff(destination, userId);
    if (existing) {
      const existingIssuedAt = new Date(existing.issuedAt).getTime();
      const nextIssuedAt = new Date(sanitized.issuedAt).getTime();

      if (existingIssuedAt > nextIssuedAt) {
        return;
      }
    }

    window.localStorage.setItem(getStorageKey(destination, userId), JSON.stringify(sanitized));
  } catch {
    // noop
  }
}

export function clearPersistedAutomationOutcomeHandoff(
  destination: AutomationOutcomeDestination,
  userId?: string | null,
) {
  if (!userId || !canUseStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(getStorageKey(destination, userId));
  } catch {
    // noop
  }
}

export function selectLatestAutomationOutcomeHandoff(
  ...handoffs: Array<AutomationOutcomeHandoffContext | null | undefined>
): AutomationOutcomeHandoffContext | null {
  return handoffs.reduce<AutomationOutcomeHandoffContext | null>((latest, current) => {
    if (!current) {
      return latest;
    }

    if (!latest) {
      return current;
    }

    return new Date(current.issuedAt).getTime() >= new Date(latest.issuedAt).getTime()
      ? current
      : latest;
  }, null);
}

export function formatAutomationOutcomeRecordedAt(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Recorded outcome available';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getAutomationOutcomeDestinationLabel(destination: AutomationOutcomeDestination): string {
  if (destination === 'dashboard') return 'Dashboard';
  if (destination === 'rebalancing') return 'Rebalancing';
  if (destination === 'portfolio-analytics') return 'Portfolio Analytics';
  return 'Risk Intelligence';
}

export function getAutomationOutcomeContinuationLabel(
  destination: AutomationOutcomeDestination,
): string {
  if (destination === 'dashboard') return 'Review Drift';
  if (destination === 'rebalancing') return 'Continue on Rebalancing';
  if (destination === 'portfolio-analytics') return 'Check Analytics';
  return 'Set monitoring for risk';
}

export function getAutomationOutcomeStateLabel(state: AutomationOutcomeState): string {
  if (state === 'matched') return 'Matched';
  if (state === 'unmatched') return 'Unmatched';
  return 'No execution yet';
}

export function getAutomationOutcomeRecordedAnswer(
  handoff: AutomationOutcomeHandoffContext,
): string {
  if (handoff.outcomeState === 'matched' && handoff.occurredAt) {
    return formatAutomationOutcomeRecordedAt(handoff.occurredAt);
  }

  return handoff.outcomeState === 'unmatched' ? 'No recorded match yet' : 'No recorded time yet';
}

export function getAutomationOutcomeDetailLabel(
  handoff: AutomationOutcomeHandoffContext,
): string {
  return handoff.outcomeState === 'matched' ? 'Prepared advisory outcome' : 'Waiting reason';
}

export function getAutomationOutcomeDetailText(
  handoff: AutomationOutcomeHandoffContext,
): string {
  if (handoff.outcomeState === 'matched') {
    return handoff.preparedOutcomeText ?? 'Recorded advisory outcome available.';
  }

  if (handoff.outcomeState === 'unmatched') {
    return handoff.waitingReason ?? 'Pinecurl is still watching this saved rule and has not recorded a match yet.';
  }

  return handoff.waitingReason ?? 'No execution is recorded yet for this saved rule.';
}
